import cx from 'classnames';
import _ from 'lodash';
import React, { FC, useEffect, useRef, useState } from 'react';
import { generateUUID, getOnEnter, getUniqueKey } from '../_utils';
import { Input } from '../input';
import {
  defaultColors,
  defaultFullColours,
  getFullColours,
  getPrimaryColors,
  getSecondaryColors,
  whiteColor,
} from './color-constant';
import styles from './colorpicker.module.scss';

export enum PICKER_VIEW {
  FULL = 'F',
  LIMITED = 'L',
}

export interface ColorPickerProps {
  /** Additional css to be applied to color picker
   * @default ''
   */
  className?: string;

  /** default viewType which is passed
   * @default 'L'
   */
  viewType?: PICKER_VIEW.LIMITED | PICKER_VIEW.FULL;

  /** default color which is passed
   * @default ''
   */
  defaultValue?: string;

  /** Collection of colors array
   * @default []
   */
  colors?: string[];

  /** color header label title
   * @default ''
   */
  hexColorTitle?: string;

  /** color input place holder string
   * @default ''
   */
  hexColorPlaceHolder?: string;

  /** auto label
   * @default ''
   */
  autoLabel?: string;

  /**
   * Boolean to know colorpicker menu is visible(open) or not
   * @default false
   */
  isVisible?: boolean;

  /**
   * Set te visibility of colorpicker menu
   */
  setIsVisible?: (isVisible: boolean) => void;

  /**
   * Refrence of the target element for colorpicke menu
   */
  targetRef?: any;

  /**
   * Reference of the menu element for handling interactions and events
   */
  menuRef?: any;

  /** callback function to return
   * the currently selected color
   * @default _.noop
   * */
  onChange?: (color: string) => void;

  /**
   * Boolean to know empty color is visible or not
   * @default false
   */
  showEmptyColor?: boolean;
}

const renderColors = (
  colorsMap: any,
  selectColor: any,
  selectedColor: string,
  colorAriaLabelText?: string
) => (
  <div className={styles.colorScale}>
    {colorsMap.map((colors: any) => (
      <div className={styles.colorBar} key={getUniqueKey(colors)}>
        {colors.map((color: any) => (
          <div
            className={cx(styles.colorScaleBlock, {
              [styles.pickedColor]: color === selectedColor,
              [styles.whiteColor]: color === whiteColor,
            })}
            key={generateUUID()}
            style={{ backgroundColor: color }}
            onClick={() => selectColor(color)}
            tabIndex={0}
            role="option"
            aria-selected={color === selectedColor}
            aria-label={`${colorAriaLabelText || ''} ${color}`}
            onKeyDown={getOnEnter(() => selectColor(color))}
            data-testid={`color-picker-button-${color}`}
          />
        ))}
      </div>
    ))}
  </div>
);

const hexColorRegExp = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/i;

export const ColorPickerMenu: FC<ColorPickerProps> = ({
  defaultValue = '',
  viewType = PICKER_VIEW.LIMITED,
  colors = [],
  onChange = _.noop,
  className = '',
  hexColorTitle = '',
  autoLabel = '',
  hexColorPlaceHolder = '',
  isVisible = false,
  setIsVisible = _.noop,
  targetRef,
  menuRef,
  showEmptyColor = false,
  ...restProps
}: ColorPickerProps) => {
  const [currentColor, setCurrentColor] = useState(defaultValue.toUpperCase());
  const [isCustomHexColorValid, setIsCustomHexColorValid] = useState(true);

  const colorPickerMenuRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);

  const selectColor = (color: string, closeColorPickerMenu = true) => {
    setCurrentColor(color);
    if (isVisible && closeColorPickerMenu) {
      setIsVisible(false);
    }
    onChange(color);
  };

  const getDefaultPrimaryColor = () =>
    viewType === PICKER_VIEW.LIMITED
      ? getPrimaryColors(defaultColors)
      : getFullColours(defaultFullColours);
  const allColors = {
    primary: colors.length ? getPrimaryColors(colors) : getDefaultPrimaryColor(),
    secondary: colors.length ? getSecondaryColors(colors) : getSecondaryColors(defaultColors),
  };

  const handleCustomHexColorChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    customHexColor: string
  ) => {
    event.stopPropagation();
    if (hexColorRegExp.test(customHexColor)) {
      setCurrentColor(customHexColor);
      if (!isCustomHexColorValid) {
        setIsCustomHexColorValid(true);
      }
    } else if (isCustomHexColorValid) {
      setIsCustomHexColorValid(false);
    }
  };

  const onCustomHexColorEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (isCustomHexColorValid) {
      selectColor(currentColor);
    }
  };

  const handleDocumentEvent = (event: any) => {
    if (menuRef.current && menuRef.current.contains(event.target)) {
      // click handler inside select menu options
      return;
    }
    if (isVisible && isCustomHexColorValid) {
      onChange(currentColor);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleDocumentEvent);
    return () => {
      document.removeEventListener('mousedown', handleDocumentEvent);
    };
  }, [currentColor, handleDocumentEvent]);
  const selectEmptyColor = () => selectColor('');

  return (
    <div className={cx(styles.content, className)} ref={colorPickerMenuRef} {...restProps}>
      <div className={styles.colorPickerContent}>
        <div className={styles.primaryColor}>
          {renderColors(allColors.primary, selectColor, currentColor, 'Button to select color')}
        </div>
        {viewType === PICKER_VIEW.LIMITED && (
          <div className={styles.secondaryColor}>
            {renderColors(allColors.secondary, selectColor, currentColor, 'Button to select color')}
          </div>
        )}
        {showEmptyColor && (
          <div
            className={styles.emptyColorBlock}
            onClick={selectEmptyColor}
            tabIndex={0}
            role="button"
            aria-label={'no-color'}
            onKeyDown={getOnEnter(() => selectEmptyColor)}
          />
        )}
        <div>
          <div className={styles.hexColor}>{hexColorTitle}</div>
          <div className={styles.hexInputContainer}>
            <Input.Text
              ariaLabel={hexColorPlaceHolder}
              className={styles.hexInputField}
              placeholder={hexColorPlaceHolder}
              value={currentColor}
              onChange={(event, customHexColor) =>
                handleCustomHexColorChange(event, customHexColor)
              }
              hasError={!isCustomHexColorValid}
              onEnter={(event) => onCustomHexColorEnter(event as any)}
              helperText={!isCustomHexColorValid ? 'Invalid Color' : ''}
            />
            <div className={styles.autoLabelWrapper}>
              <div className={styles.selectedColor} style={{ backgroundColor: currentColor }} />
              <div className={styles.autoLabel}>{autoLabel}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
